/**
 * Filtering and grouping for the Notifications view (#624): mail for the
 * management company's own staff, not what was sent to homeowners.
 */
import { NotificationResponse } from '../clients/rust-api-client';
import { ListGroup } from '../shell/GroupedList';

export interface StaffNotificationFilters {
  recipients: string[];
  hoas: string[];
  types: string[];
  search: string;
}

/** Your own notifications, until you widen it. */
export const defaultStaffFilters = (
  myUserId: string | null
): StaffNotificationFilters => ({
  recipients: myUserId ? [myUserId] : [],
  hoas: [],
  types: [],
  search: '',
});

export const applyStaffFilters = (
  rows: NotificationResponse[],
  f: StaffNotificationFilters
): NotificationResponse[] => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((n) => {
    if (f.recipients.length && !f.recipients.includes(n.user_id ?? ''))
      return false;
    if (f.hoas.length && !f.hoas.includes(n.hoa_id)) return false;
    if (f.types.length && !f.types.includes(n.notification_type)) return false;
    if (q) {
      const hay = [n.title, n.message, n.recipient_name, n.hoa_name]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

/** Each staff recipient present, by name; yours first, as "Me". */
export const recipientOptions = (
  rows: NotificationResponse[],
  myUserId: string | null
) => {
  const byId = new Map<string, string>();
  rows.forEach((n) => n.user_id && byId.set(n.user_id, n.recipient_name));
  const options = Array.from(byId, ([value, label]) => ({
    value,
    label: value === myUserId ? 'Me' : label,
  }));
  return options.sort((a, b) =>
    a.value === myUserId
      ? -1
      : b.value === myUserId
        ? 1
        : a.label.localeCompare(b.label)
  );
};

/** Unread, then read; newest first within each. */
export const groupByReadState = (
  rows: NotificationResponse[]
): ListGroup<NotificationResponse>[] => {
  const newest = [...rows].sort((a, b) =>
    b.created_at.localeCompare(a.created_at)
  );
  return [
    { key: 'unread', label: 'Unread', items: newest.filter((n) => !n.is_read) },
    { key: 'read', label: 'Read', items: newest.filter((n) => n.is_read) },
  ].filter((g) => g.items.length > 0);
};
